import { useEffect, useState } from "react";
import { api, bridgeApi } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Link2, Loader2, RefreshCw, Pencil, Check, KeyRound, FileDown } from "lucide-react";

const STATUS_META = {
  LIVE: { color: "text-emerald-300 border-emerald-500/40", dot: "bg-emerald-400" },
  DEPLOYED_SIGNATURE_REJECTED: { color: "text-amber-300 border-amber-500/40", dot: "bg-amber-400" },
  HOST_LIVE_ENDPOINTS_MISSING: { color: "text-amber-300 border-amber-500/40", dot: "bg-amber-400" },
  UNREACHABLE: { color: "text-rose-300 border-rose-500/40", dot: "bg-rose-400" },
  NOT_CONFIGURED: { color: "text-white/40 border-white/15", dot: "bg-white/30" },
};

// BRIDGE HANDSHAKE CONSOLE — one-click signed HMAC probe of every sibling estate.
// Truthful states only; results filed to estate memory + MASTER_STATE. Super-admin only.
export default function BridgeHandshake() {
  const { user } = useAuth();
  const [results, setResults] = useState(null);
  const [probing, setProbing] = useState(false);
  const [allowed, setAllowed] = useState(true);
  const [probedAt, setProbedAt] = useState(null);
  const [editing, setEditing] = useState(null);
  const [urlDraft, setUrlDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [secretFor, setSecretFor] = useState(null);
  const [secretDraft, setSecretDraft] = useState("");
  const [overrides, setOverrides] = useState({});
  const [kitBusy, setKitBusy] = useState(false);
  const isSuper = Boolean(user?.is_super_admin);

  useEffect(() => {
    if (isSuper) bridgeApi.secrets().then((r) => setOverrides(r.overrides || {})).catch(() => {});
  }, [isSuper]);

  const saveSecret = async (app) => {
    setSaving(true);
    try {
      const r = await bridgeApi.setSecret(app, secretDraft.trim());
      setOverrides(r.overrides || {});
      setSecretFor(null);
      setSecretDraft("");
      toast.success(secretDraft.trim()
        ? `${app.replace("_", " ")} secret filed (encrypted, never shown again) — force-sync probing…`
        : `${app.replace("_", " ")} override cleared — env fallback`);
      await probe();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Secret filing failed");
    } finally { setSaving(false); }
  };

  const downloadKit = async () => {
    setKitBusy(true);
    try {
      const r = await bridgeApi.alignmentKit();
      toast.success("Alignment Kit sealed — production-ready deployment artifact");
      window.open(`${process.env.REACT_APP_BACKEND_URL}${r.download}`, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kit generation failed");
    } finally { setKitBusy(false); }
  };

  const saveTarget = async (app) => {
    setSaving(true);
    try {
      await bridgeApi.setTarget(app, urlDraft.trim());
      toast.success(`${app.replace("_", " ")} deployment URL registered — probing…`);
      setEditing(null);
      await probe();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "URL rejected");
    } finally { setSaving(false); }
  };

  // Last known statuses from estate memory (bridge_* facts)
  useEffect(() => {
    if (!isSuper) return;
    api.get("/estate-memory")
      .then((r) => {
        const facts = (r.data.facts || []).filter((f) =>
          ["bridge_gifted_os", "bridge_checkmate_payroll"].includes(f.key));
        if (facts.length) {
          setResults(facts.map((f) => ({
            app: f.key.replace("bridge_", ""), status: f.status, detail: f.evidence,
          })));
        }
      })
      .catch(() => {});
  }, [isSuper]);

  const probe = async () => {
    setProbing(true);
    try {
      const r = await bridgeApi.handshake();
      setResults(r.results);
      setProbedAt(r.at);
      toast[r.live_count > 0 ? "success" : "warning"](
        `Handshake complete — ${r.live_count}/${r.results.length} sibling(s) LIVE`);
    } catch (e) {
      if (e?.response?.status === 403) { setAllowed(false); return; }
      toast.error(e?.response?.data?.detail || "Handshake probe failed");
    } finally { setProbing(false); }
  };

  if (!isSuper || !allowed) return null;

  return (
    <section className="uc-card p-4" data-testid="bridge-handshake-console">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Link2 size={12} className="text-fuchsia-300" />
        <span className="font-mono-uc text-[10px] uppercase tracking-widest text-white/80">
          Sovereign Bridge · Handshake Console
        </span>
        <span className="font-mono-uc text-[9px] text-white/40" data-testid="bridge-autoprobe-note">
          auto-probe hourly · alerts on LIVE flip
        </span>
        {probedAt && (
          <span className="font-mono-uc text-[9px] text-white/40">
            probed {new Date(probedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
        <button
          data-testid="bridge-alignment-kit-btn"
          onClick={downloadKit}
          disabled={kitBusy}
          title="Seal a production-ready deployment kit (env var + HMAC spec + verification script) for sibling operators"
          className="ml-auto uc-chip flex items-center gap-1.5 hover:border-amber-500/60 hover:bg-amber-500/10 text-amber-300 disabled:opacity-50"
        >
          {kitBusy ? <Loader2 size={10} className="animate-spin" /> : <FileDown size={10} />}
          Alignment Kit
        </button>
        <button
          data-testid="bridge-handshake-btn"
          onClick={probe}
          disabled={probing}
          className="uc-chip flex items-center gap-1.5 hover:border-fuchsia-500/60 hover:bg-fuchsia-500/10 text-fuchsia-300 disabled:opacity-50"
        >
          {probing ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
          Run Signed Handshake
        </button>
      </div>

      {!results && (
        <div className="text-white/40 italic text-xs" data-testid="bridge-handshake-empty">
          No probe on record — run the signed handshake to verify sibling estates live.
        </div>
      )}

      {results && (
        <div className="space-y-1.5" data-testid="bridge-handshake-results">
          {results.map((r) => {
            const m = STATUS_META[r.status] || STATUS_META.NOT_CONFIGURED;
            return (
              <div key={r.app} className="flex items-center gap-3 border border-white/5 px-3 py-2 flex-wrap"
                   data-testid={`bridge-status-${r.app}`}>
                <span className={`uc-dot ${m.dot} ${r.status === "LIVE" ? "uc-pulsing" : ""}`}></span>
                <span className="font-mono-uc text-[10px] uppercase tracking-widest text-white/85">
                  {r.app.replace("_", " ")}
                </span>
                <span className={`font-mono-uc text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${m.color}`}
                      data-testid={`bridge-status-badge-${r.app}`}>
                  {r.status.replace(/_/g, " ")}
                </span>
                {r.endpoint && <span className="font-mono-uc text-[9px] text-emerald-300">{r.endpoint}</span>}
                {overrides[r.app] && (
                  <span className="font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-sky-500/40 text-sky-300"
                        data-testid={`bridge-secret-override-${r.app}`}>
                    Secret Override
                  </span>
                )}
                <button
                  data-testid={`bridge-secret-btn-${r.app}`}
                  title="FORCE-SYNC: file this sibling's OWN shared secret (encrypted) — Umbrella signs with it, no sibling redeploy needed"
                  onClick={() => { setSecretFor(secretFor === r.app ? null : r.app); setSecretDraft(""); }}
                  className="ml-auto text-white/40 hover:text-sky-300 transition-colors"
                >
                  <KeyRound size={11} />
                </button>
                <button
                  data-testid={`bridge-edit-url-${r.app}`}
                  title="Register / change the sibling deployment URL"
                  onClick={() => { setEditing(editing === r.app ? null : r.app); setUrlDraft(r.url || ""); }}
                  className="text-white/40 hover:text-amber-400 transition-colors"
                >
                  <Pencil size={11} />
                </button>
                {r.detail && (
                  <span className="basis-full font-mono-uc text-[9px] text-white/45 leading-relaxed">{r.detail}</span>
                )}
                {secretFor === r.app && (
                  <div className="basis-full flex gap-1.5 mt-1" data-testid={`bridge-secret-editor-${r.app}`}>
                    <input
                      data-testid={`bridge-secret-input-${r.app}`}
                      type="password"
                      value={secretDraft}
                      onChange={(e) => setSecretDraft(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveSecret(r.app)}
                      placeholder="Paste this sibling's BRIDGE_SHARED_SECRET (encrypted at rest, never shown again; empty = clear)"
                      className="flex-1 h-8 bg-black border border-white/15 focus:border-sky-500/40 outline-none px-2 text-[11px] text-white/90 font-mono-uc"
                    />
                    <button
                      data-testid={`bridge-secret-save-${r.app}`}
                      onClick={() => saveSecret(r.app)}
                      disabled={saving}
                      className="w-8 h-8 border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 flex items-center justify-center disabled:opacity-40"
                    >
                      {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                    </button>
                  </div>
                )}
                {editing === r.app && (
                  <div className="basis-full flex gap-1.5 mt-1" data-testid={`bridge-url-editor-${r.app}`}>
                    <input
                      data-testid={`bridge-url-input-${r.app}`}
                      value={urlDraft}
                      onChange={(e) => setUrlDraft(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveTarget(r.app)}
                      placeholder="https://sibling-app.emergent.host/api"
                      className="flex-1 h-8 bg-black border border-white/15 focus:border-amber-500/40 outline-none px-2 text-[11px] text-white/90 font-mono-uc"
                    />
                    <button
                      data-testid={`bridge-url-save-${r.app}`}
                      onClick={() => saveTarget(r.app)}
                      disabled={saving}
                      className="w-8 h-8 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 flex items-center justify-center disabled:opacity-40"
                    >
                      {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
